import { Input } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { forwardRef, useMemo } from 'react';
import { NumericFormat as NumberFormat } from 'react-number-format';

const useStyles = makeStyles((theme: any) => ({
  inputClassName: {
    color: theme.palette.primary.darker,
    fontWeight: 500,
    paddingRight: theme.spacing(1),
    '&&&:before': { borderBottom: 'none' },
    '&&:after': { borderBottom: 'none' },
    '&:hover': { background: theme.palette.input.border },
    width: '100%',
  },
  disabledClass: {
    color: theme.palette.primary.darker,
    background: theme.palette.background.default,
    cursor: 'not-allowed'
  },
}));

type Props = {
  value: number | undefined,
  onChange: (value: number) => void,
  isDisabled?: boolean,
  ariaLabel?: string,
}

export const MoneyField = ({
  value,
  isDisabled = false,
  onChange,
  ariaLabel
}: Props) => {
  const { disabledClass, inputClassName } = useStyles();

  // MUI hands its input ref to inputComponent as `ref`, so this must forward it to react-number-format.
  const NumberFormatCustom = useMemo(() => forwardRef<HTMLInputElement, any>((props, ref) => {
    const formatValue = (_value: string) => {
      let amount = Number(_value.replace('$', '').replace('-$', ''));
      if (Number.isNaN(amount)) {
        amount = 0;
      }
      return amount;
    };

    return (
      <NumberFormat
        {...props}
        getInputRef={ref}
        onValueChange={(values) => {
          onChange(formatValue(values.value));
        }}
        decimalScale={2}
        thousandSeparator
        allowNegative
        prefix="$"
        isAllowed={({ floatValue = 0 }) => floatValue >= -9_999_999_999_999.99 && floatValue <= 9_999_999_999_999.99}
      />
    );
  }), [onChange]);

  return (
    <Input
      className={inputClassName}
      disabled={isDisabled}
      value={value}
      classes={{ disabled: disabledClass }}
      inputProps={{ style: { textAlign: 'right' }, 'aria-label': ariaLabel }}
      inputComponent={NumberFormatCustom}
    />
  );
};
